import { useCallback, useContext, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { AuthContext } from "context";
import { buildRoute } from "routes";
import type { CatalogService } from "types/service";

function useOpenProviderConversation() {
    const { account } = useContext(AuthContext);
    const navigate = useNavigate();

    return useCallback(
        (serviceId: string, event?: MouseEvent) => {
            event?.stopPropagation();
            const clientId = account?.resources?.client?.[0]?.id;
            if (clientId) {
                navigate(buildRoute.messaging(serviceId, clientId));
            } else {
                navigate(buildRoute.loginWithRedirect(window.location.pathname + window.location.search));
            }
        },
        [account, navigate],
    );
}

export function useProviderContact(serviceId: string) {
    const openConversation = useOpenProviderConversation();

    return useCallback(
        (event?: MouseEvent) => {
            openConversation(serviceId, event);
        },
        [openConversation, serviceId],
    );
}

export function useProviderServiceContact(catalog: CatalogService[], fallbackServiceId: string) {
    const { account } = useContext(AuthContext);
    const openConversation = useOpenProviderConversation();
    const [open, setOpen] = useState(false);

    const onContactClick = useCallback(
        (event?: MouseEvent) => {
            event?.stopPropagation();
            const clientId = account?.resources?.client?.[0]?.id;
            if (!clientId || catalog.length <= 1) {
                openConversation(catalog[0]?.id ?? fallbackServiceId, event);
                return;
            }

            setOpen(true);
        },
        [account, catalog, fallbackServiceId, openConversation],
    );

    const contactService = useCallback(
        (serviceId: string) => {
            setOpen(false);
            openConversation(serviceId);
        },
        [openConversation],
    );

    return { open, setOpen, onContactClick, contactService };
}
